'use client';

import {
  Award,
  BookOpen,
  ChevronDown,
  FileText,
  Home,
  MapPin,
  Menu,
  MessageSquare,
  Phone,
  Users,
  X,
} from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';
import { CalendlyScheduleButton } from '@/components/calendly/CalendlyScheduleButton';
import { GBP_BUSINESS_NAME, SITE_PHONE_DISPLAY, SITE_PHONE_TEL_HREF } from '@/lib/site-contact';

const navigation = {
  main: [
    { name: 'Home', href: '/', icon: Home },
    { name: 'Services', href: '/services', icon: FileText },
    { name: 'Locations', href: '/locations', icon: MapPin },
    { name: 'Resources', href: '/resources', icon: BookOpen },
    { name: 'Blog', href: '/blog', icon: BookOpen },
    { name: 'FAQ', href: '/faq', icon: BookOpen },
    { name: 'About', href: '/about', icon: Users },
    { name: 'Contact', href: '/contact', icon: MessageSquare },
  ],
  blog: [
    { name: 'All Posts', href: '/blog', description: 'Latest blog posts and articles' },
    {
      name: 'Probate Videos',
      href: '/blog/probate-videos',
      description: 'Educational video content',
    },
    {
      name: 'Market Updates',
      href: '/blog/market-updates',
      description: 'Real estate market trends',
    },
  ],
  locations: [
    {
      name: 'All Nevada Locations',
      href: '/locations',
      description: 'Complete coverage across Nevada',
    },
    {
      name: 'Las Vegas',
      href: '/locations/las-vegas',
      description: 'Downtown & Strip area probate services',
    },
    {
      name: 'Henderson',
      href: '/locations/henderson',
      description: 'Green Valley & Anthem probate expertise',
    },
    {
      name: 'Summerlin',
      href: '/locations/summerlin',
      description: 'Master-planned community probate real estate',
    },
    {
      name: 'North Las Vegas',
      href: '/locations/north-las-vegas',
      description: 'Aliante & Centennial Hills services',
    },
    {
      name: 'Boulder City',
      href: '/locations/boulder-city',
      description: 'Historic area probate management',
    },
    {
      name: 'Mesquite',
      href: '/locations/mesquite',
      description: 'Golf community probate real estate',
    },
    {
      name: 'Spring Valley',
      href: '/locations/spring-valley',
      description: 'Residential area probate services',
    },
    {
      name: 'Enterprise',
      href: '/locations/enterprise',
      description: 'Southwest Las Vegas probate expertise',
    },
  ],
  resources: [
    {
      name: 'Nevada Probate Guide',
      href: '/resources/nevada-probate-guide',
      description: 'Complete guide to Nevada probate process',
    },
    {
      name: 'Las Vegas Guide',
      href: '/resources/las-vegas-probate-guide',
      description: 'Las Vegas & Clark County probate guide',
    },
    {
      name: 'Legal resources',
      href: '/resources/las-vegas-probate-legal-resources',
      description: 'Courts, self-help & legal aid',
    },
    {
      name: 'FAQ & Q&A',
      href: '/faq',
      description: 'Comprehensive probate real estate questions & answers',
    },
    {
      name: 'Probate Timeline',
      href: '/resources/nevada-probate-guide#timeline',
      description: '6-8 month Nevada probate timeline',
    },
    {
      name: 'Probate Costs',
      href: '/resources/nevada-probate-guide#costs',
      description: 'Nevada probate costs breakdown',
    },
    {
      name: 'Court Process',
      href: '/resources/nevada-probate-guide#process',
      description: 'Clark County court procedures',
    },
  ],
  services: [
    {
      name: 'Probate Real Estate Sales',
      href: '/services/probate-real-estate-sales/',
      description: 'Full-service probate sales in Clark County',
    },
    {
      name: 'Estate Liquidation',
      href: '/services/estate-liquidation/',
      description: 'Estate, trust, and multi-parcel liquidation',
    },
    {
      name: 'Court-Approved Property Sales',
      href: '/services/court-approved-property-sales/',
      description: 'Nevada court-confirmed probate sales',
    },
    {
      name: 'Trust Sale Services',
      href: '/services/trust-sale-services/',
      description: 'Trust-owned property sales in 30-45 days',
    },
    {
      name: 'Probate Property Valuation',
      href: '/services/probate-property-valuation/',
      description: 'Real Estate Appraiser valuation services',
    },
    {
      name: 'Estate Property CMA Reports',
      href: '/services/estate-property-cma-reports/',
      description: 'Detailed CMAs for executors and attorneys',
    },
    {
      name: 'Estate Attorney Referral Services',
      href: '/services/estate-attorney-referral-services/',
      description: 'Coordinated support for probate counsel',
    },
    {
      name: 'Partition Action Consulting',
      href: '/services/partition-action-consulting/',
      description: 'Court-ordered partition sale consulting',
    },
    {
      name: 'Executor Property Consulting',
      href: '/services/executor-property-consulting/',
      description: 'Free confidential executor consulting',
    },
    {
      name: 'Probate Division',
      href: '/probate-division/',
      description: 'Dedicated probate & trust division',
    },
    {
      name: 'Probate Homes for Sale',
      href: '/probate-homes-for-sale/',
      description: 'Browse probate listings',
    },
    {
      name: 'Trust Property Sales',
      href: '/trust-property-sales/',
      description: 'Trust & estate property sales',
    },
    {
      name: 'Conservatorship Real Estate',
      href: '/conservatorship-real-estate/',
      description: 'Court-approved conservatorship sales',
    },
    {
      name: 'Probate Property Buyers',
      href: '/probate-property-buyers/',
      description: 'Sell probate property quickly',
    },
    {
      name: 'Probate Estate Sales',
      href: '/probate-estate-sales/',
      description: 'Complete estate liquidation',
    },
    {
      name: 'All Services',
      href: '/services/',
      description: 'Full service overview',
    },
  ],
};

export default function Navigation() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [locationsOpen, setLocationsOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [blogOpen, setBlogOpen] = useState(false);

  return (
    <nav className="bg-white/95 backdrop-blur-sm shadow-lg sticky top-0 z-50 border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link
              href="/"
              title={GBP_BUSINESS_NAME}
              className="flex items-center hover:opacity-80 transition-opacity focus-ring rounded-md p-1 max-w-[min(100%,14rem)] sm:max-w-none"
              aria-label={`${GBP_BUSINESS_NAME} — home`}
            >
              <Award className="h-8 w-8 text-primary-700 shrink-0" />
              <span className="ml-2 text-lg sm:text-xl font-bold text-secondary-900 leading-tight">
                Probate Real Estate Sales
              </span>
              <span className="ml-2 text-xs font-semibold text-secondary-500 uppercase tracking-wide hidden sm:block">
                Las Vegas
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex lg:items-center lg:space-x-4">
            {/* Phone Number - Prominent */}
            <div className="flex items-center gap-2 px-3 py-2 bg-primary-50 rounded-lg">
              <Phone className="h-4 w-4 text-primary-700" />
              <a
                href={SITE_PHONE_TEL_HREF}
                className="text-primary-700 font-semibold text-sm hover:text-primary-900 transition-colors focus-ring rounded-md px-1"
                aria-label={`Call Probate Real Estate Sales at ${SITE_PHONE_DISPLAY}`}
              >
                {SITE_PHONE_DISPLAY}
              </a>
            </div>

            {/* Main Navigation */}
            <Link
              href="/"
              className="text-secondary-700 hover:text-primary-900 px-3 py-2 text-sm font-medium transition-colors focus-ring rounded-md"
              aria-label="Home page"
            >
              Home
            </Link>

            {/* Services Dropdown */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setServicesOpen(!servicesOpen)}
                className="text-secondary-700 hover:text-primary-900 px-3 py-2 text-sm font-medium transition-colors flex items-center focus-ring rounded-md"
                aria-label="Services menu"
                aria-expanded={servicesOpen}
              >
                Services
                <ChevronDown className="ml-1 h-4 w-4" />
              </button>

              {servicesOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl ring-1 ring-black ring-opacity-5 z-50 border border-gray-100">
                  <div className="py-2">
                    {navigation.services.map((item) => (
                      <Link
                        key={item.name}
                        href={item.href}
                        className="block px-4 py-3 text-sm text-secondary-700 hover:bg-primary-50 hover:text-primary-900 transition-colors focus-ring rounded-md mx-1"
                        onClick={() => setServicesOpen(false)}
                        aria-label={`${item.name} - ${item.description}`}
                      >
                        <div className="font-medium">{item.name}</div>
                        <div className="text-secondary-500 text-xs">{item.description}</div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Locations Dropdown */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setLocationsOpen(!locationsOpen)}
                className="text-secondary-700 hover:text-primary-900 px-3 py-2 text-sm font-medium transition-colors flex items-center focus-ring rounded-md"
                aria-label="Locations menu"
                aria-expanded={locationsOpen}
              >
                Locations
                <ChevronDown className="ml-1 h-4 w-4" />
              </button>

              {locationsOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl ring-1 ring-black ring-opacity-5 z-50 border border-gray-100">
                  <div className="py-2">
                    {navigation.locations.map((item) => (
                      <Link
                        key={item.name}
                        href={item.href}
                        className="block px-4 py-3 text-sm text-secondary-700 hover:bg-primary-50 hover:text-primary-900 transition-colors focus-ring rounded-md mx-1"
                        onClick={() => setLocationsOpen(false)}
                        aria-label={`${item.name} - ${item.description}`}
                      >
                        <div className="font-medium">{item.name}</div>
                        <div className="text-secondary-500 text-xs">{item.description}</div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Resources Dropdown */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setResourcesOpen(!resourcesOpen)}
                className="text-secondary-700 hover:text-primary-900 px-3 py-2 text-sm font-medium transition-colors flex items-center focus-ring rounded-md"
                aria-label="Resources menu"
                aria-expanded={resourcesOpen}
              >
                Resources
                <ChevronDown className="ml-1 h-4 w-4" />
              </button>

              {resourcesOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl ring-1 ring-black ring-opacity-5 z-50 border border-gray-100">
                  <div className="py-2">
                    {navigation.resources.map((item) => (
                      <Link
                        key={item.name}
                        href={item.href}
                        className="block px-4 py-3 text-sm text-secondary-700 hover:bg-primary-50 hover:text-primary-900 transition-colors focus-ring rounded-md mx-1"
                        onClick={() => setResourcesOpen(false)}
                        aria-label={`${item.name} - ${item.description}`}
                      >
                        <div className="font-medium">{item.name}</div>
                        <div className="text-secondary-500 text-xs">{item.description}</div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Blog Dropdown */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setBlogOpen(!blogOpen)}
                className="text-secondary-700 hover:text-primary-900 px-3 py-2 text-sm font-medium transition-colors flex items-center focus-ring rounded-md"
                aria-label="Blog menu"
                aria-expanded={blogOpen}
              >
                Blog
                <ChevronDown className="ml-1 h-4 w-4" />
              </button>

              {blogOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl ring-1 ring-black ring-opacity-5 z-50 border border-gray-100">
                  <div className="py-2">
                    {navigation.blog.map((item) => (
                      <Link
                        key={item.name}
                        href={item.href}
                        className="block px-4 py-3 text-sm text-secondary-700 hover:bg-primary-50 hover:text-primary-900 transition-colors focus-ring rounded-md mx-1"
                        onClick={() => setBlogOpen(false)}
                        aria-label={`${item.name} - ${item.description}`}
                      >
                        <div className="font-medium">{item.name}</div>
                        <div className="text-secondary-500 text-xs">{item.description}</div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* About Link */}
            <Link
              href="/about/"
              className="text-secondary-700 hover:text-primary-900 px-3 py-2 text-sm font-medium transition-colors focus-ring rounded-md"
              aria-label="About Dr. Jan Duffy and our team"
            >
              About
            </Link>

            {/* Contact Link */}
            <Link
              href="/contact/"
              className="text-secondary-700 hover:text-primary-900 px-3 py-2 text-sm font-medium transition-colors focus-ring rounded-md"
              aria-label="Contact us"
            >
              Contact
            </Link>

            {/* CTA Buttons */}
            <CalendlyScheduleButton
              variant="outline"
              className="hidden lg:inline-flex border-primary-600 text-primary-700"
            />
            <Link
              href="https://drjanduffy.realscout.com/onboarding"
              className="bg-primary-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-700 hover-lift transition-all duration-200 focus-ring"
              aria-label="Get your free probate consultation"
              rel="noopener noreferrer"
            >
              Free Consultation
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden flex items-center gap-3">
            {/* Mobile Phone Link */}
            <a
              href={SITE_PHONE_TEL_HREF}
              className="flex items-center gap-1 px-3 py-1.5 bg-primary-50 rounded-md text-primary-700 font-semibold text-sm hover:bg-primary-100 transition-colors focus-ring"
              aria-label={`Call ${SITE_PHONE_DISPLAY}`}
            >
              <Phone className="h-4 w-4" />
              <span className="hidden sm:inline">{SITE_PHONE_DISPLAY}</span>
            </a>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-secondary-700 hover:text-primary-900 p-2 focus-ring rounded-md transition-colors"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-secondary-200 animate-slide-up">
          <div className="px-2 pt-2 pb-3 space-y-1 max-h-[calc(100vh-4rem)] overflow-y-auto">
            {/* Main Navigation */}
            <Link
              href="/"
              className="block px-3 py-2 text-base font-medium text-secondary-700 hover:text-primary-900 hover:bg-primary-50 rounded-md transition-colors focus-ring"
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Home page"
            >
              Home
            </Link>
            <Link
              href="/faq/"
              className="block px-3 py-2 text-base font-medium text-secondary-700 hover:text-primary-900 hover:bg-primary-50 rounded-md transition-colors focus-ring"
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Frequently asked questions"
            >
              FAQ
            </Link>

            {/* Services Section */}
            <div className="border-t border-secondary-200 pt-4">
              <div className="px-3 py-2 text-xs font-semibold text-secondary-500 uppercase tracking-wider">
                Services
              </div>
              {navigation.services.map((item) => (
                <Link
                  key={item.name}
                  href={item.href}
                  className="block px-3 py-2 text-base font-medium text-secondary-700 hover:text-primary-900 hover:bg-primary-50 rounded-md transition-colors focus-ring"
                  onClick={() => setMobileMenuOpen(false)}
                  aria-label={`${item.name} - ${item.description}`}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* Locations Section */}
            <div className="border-t border-secondary-200 pt-4">
              <div className="px-3 py-2 text-xs font-semibold text-secondary-500 uppercase tracking-wider">
                Nevada Locations
              </div>
              {navigation.locations.map((item) => (
                <Link
                  key={item.name}
                  href={item.href}
                  className="block px-3 py-2 text-base font-medium text-secondary-700 hover:text-primary-900 hover:bg-primary-50 rounded-md transition-colors focus-ring"
                  onClick={() => setMobileMenuOpen(false)}
                  aria-label={`${item.name} - ${item.description}`}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* Resources Section */}
            <div className="border-t border-secondary-200 pt-4">
              <div className="px-3 py-2 text-xs font-semibold text-secondary-500 uppercase tracking-wider">
                Resources
              </div>
              {navigation.resources.map((item) => (
                <Link
                  key={item.name}
                  href={item.href}
                  className="block px-3 py-2 text-base font-medium text-secondary-700 hover:text-primary-900 hover:bg-primary-50 rounded-md transition-colors focus-ring"
                  onClick={() => setMobileMenuOpen(false)}
                  aria-label={`${item.name} - ${item.description}`}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* Blog Section */}
            <div className="border-t border-secondary-200 pt-4">
              <div className="px-3 py-2 text-xs font-semibold text-secondary-500 uppercase tracking-wider">
                Blog
              </div>
              {navigation.blog.map((item) => (
                <Link
                  key={item.name}
                  href={item.href}
                  className="block px-3 py-2 text-base font-medium text-secondary-700 hover:text-primary-900 hover:bg-primary-50 rounded-md transition-colors focus-ring"
                  onClick={() => setMobileMenuOpen(false)}
                  aria-label={`${item.name} - ${item.description}`}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* About Link */}
            <Link
              href="/about/"
              className="block px-3 py-2 text-base font-medium text-secondary-700 hover:text-primary-900 hover:bg-primary-50 rounded-md border-t border-secondary-200 pt-4 transition-colors focus-ring"
              onClick={() => setMobileMenuOpen(false)}
              aria-label="About Dr. Jan Duffy and our team"
            >
              About
            </Link>

            {/* Contact Link */}
            <Link
              href="/contact/"
              className="block px-3 py-2 text-base font-medium text-secondary-700 hover:text-primary-900 hover:bg-primary-50 rounded-md transition-colors focus-ring"
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Contact us"
            >
              Contact
            </Link>

            {/* Mobile CTA */}
            <div className="border-t border-secondary-200 pt-4 space-y-3">
              <div className="flex justify-center">
                <CalendlyScheduleButton
                  variant="outline"
                  className="w-full sm:w-auto justify-center"
                  onScheduleClick={() => setMobileMenuOpen(false)}
                />
              </div>
              <Link
                href="https://drjanduffy.realscout.com/onboarding"
                className="block w-full text-center bg-primary-600 text-white px-4 py-3 rounded-md text-base font-medium hover:bg-primary-700 hover-lift transition-all duration-200 focus-ring"
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Get your free probate consultation"
                rel="noopener noreferrer"
              >
                Free Consultation
              </Link>
            </div>

            {/* Contact Info */}
            <div className="border-t border-secondary-200 pt-4 px-3">
              <div className="text-sm text-secondary-600">
                <div className="flex items-center gap-2 mb-2">
                  <Phone className="h-4 w-4" />
                  <a href={SITE_PHONE_TEL_HREF} className="text-primary-700 font-medium">
                    {SITE_PHONE_DISPLAY}
                  </a>
                </div>
                <div className="text-xs text-secondary-500">
                  Nevada's Fastest Probate: 6-8 Months
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
